<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Feature Flag Missing Default Value
                            </h2>
                        </div>
                    </div>
                    <project-change-notice
                        :project-key="current.projectKey"
                        @refresh="load"
                    />

                    <div
                        class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                    >
                        <div>
                            <label
                                for="missing-default-filter-environment"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Environment
                            </label>
                            <select
                                id="missing-default-filter-environment"
                                v-model="filters.environmentKey"
                                data-testid="missing-default-filter-environment"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            >
                                <option
                                    v-for="environment in environments"
                                    :key="environment.key"
                                    :value="environment.key"
                                >
                                    {{ environment.name }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label
                                for="missing-default-filter-usage-status"
                                class="block text-sm font-medium text-taupe-700 mb-1"
                            >
                                Usage
                            </label>
                            <select
                                id="missing-default-filter-usage-status"
                                v-model="filters.usageStatus"
                                data-testid="missing-default-filter-usage-status"
                                class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                            >
                                <option value="">All flags</option>
                                <option
                                    v-for="option in usageStatusOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <zen-button
                                variant="secondary"
                                size="field"
                                data-testid="missing-default-apply-filters"
                                @click="applyFilters"
                            >
                                Apply
                            </zen-button>
                        </div>
                    </div>

                    <zen-loading-indicator v-if="loading" />

                    <zen-message-block v-if="error" type="error">
                        {{
                            error.message ||
                            'Failed to load missing default value report. Please try again.'
                        }}
                    </zen-message-block>

                    <template v-if="!loading && !error">
                        <dl
                            class="grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-taupe-50 ring-1 ring-taupe-200"
                            data-testid="missing-default-summary"
                        >
                            <div
                                v-for="tile in summaryTiles"
                                :key="tile.key"
                                class="bg-white px-4 py-4 sm:px-5 sm:py-4"
                            >
                                <dt
                                    class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                >
                                    {{ tile.label }}
                                </dt>
                                <dd
                                    class="mt-1.5 text-xl font-medium text-taupe-800"
                                    :data-testid="
                                        'missing-default-summary-' + tile.key
                                    "
                                >
                                    {{ summary[tile.key] ?? 0 }}
                                </dd>
                            </div>
                        </dl>

                        <div
                            class="-mx-4 mt-8 overflow-hidden shadow-sm sm:-mx-6 md:mx-0 md:rounded-lg"
                        >
                            <table class="min-w-full divide-y divide-taupe-300">
                                <thead class="bg-taupe-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            class="w-full lg:w-3/4 py-3.5 pl-4 pr-3 text-left text-sm lg:text-base font-medium text-taupe-800 sm:pl-6"
                                        >
                                            <button
                                                type="button"
                                                class="flex items-center space-x-1 hover:text-sky-500"
                                                @click="toggleSort('name')"
                                            >
                                                <span>Flag</span>
                                                <span
                                                    v-if="
                                                        filters.sortBy ===
                                                        'name'
                                                    "
                                                    >{{ sortIndicator }}</span
                                                >
                                            </button>
                                        </th>
                                        <th
                                            scope="col"
                                            class="w-1/4 hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                        >
                                            <button
                                                type="button"
                                                class="flex items-center space-x-1 hover:text-sky-500"
                                                @click="
                                                    toggleSort('last_used_at')
                                                "
                                            >
                                                <span>Last Used</span>
                                                <span
                                                    v-if="
                                                        filters.sortBy ===
                                                        'last_used_at'
                                                    "
                                                    >{{ sortIndicator }}</span
                                                >
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody
                                    class="divide-y divide-taupe-200 bg-white"
                                >
                                    <tr
                                        v-for="flag in flags"
                                        :key="flag.flag_ulid"
                                        :class="
                                            !flag.never_used ? 'bg-sky-50' : ''
                                        "
                                        :data-testid="
                                            'missing-default-row-' +
                                            flag.flag_ulid
                                        "
                                    >
                                        <td
                                            class="w-full max-w-0 py-4 pl-4 pr-3 text-sm lg:text-base font-medium text-taupe-800 sm:w-auto sm:max-w-none sm:pl-6"
                                        >
                                            <div>
                                                {{ flag.flag_name }}
                                                <span
                                                    v-if="flag.never_used"
                                                    class="ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-taupe-100 text-taupe-800"
                                                    data-testid="missing-default-never-used-badge"
                                                >
                                                    Never used
                                                </span>
                                            </div>
                                            <div
                                                class="font-light font-mono text-sm text-taupe-800"
                                            >
                                                <router-link
                                                    v-if="flag.flag_key"
                                                    :to="{
                                                        name: 'ProjectFlagDetailScreen',
                                                        params: {
                                                            projectKey:
                                                                current.projectKey,
                                                            environmentKey:
                                                                environmentKeyForLinks,
                                                            key: flag.flag_key,
                                                        },
                                                    }"
                                                    class="flex space-x-1 text-taupe-500 hover:text-sky-500"
                                                >
                                                    <span>{{
                                                        flag.flag_key
                                                    }}</span>
                                                    <zen-icon-open-link
                                                        css-classes="h-4 w-4"
                                                        stroke-width="2"
                                                    />
                                                </router-link>
                                                <span v-else>—</span>
                                            </div>
                                            <dl class="font-light lg:hidden">
                                                <dt class="sr-only">
                                                    Last Used
                                                </dt>
                                                <dd
                                                    class="mt-1 truncate text-taupe-800"
                                                >
                                                    <zen-date-time
                                                        v-if="flag.last_used_at"
                                                        human
                                                        :value="
                                                            flag.last_used_at
                                                        "
                                                    />
                                                    <span v-else>Never</span>
                                                </dd>
                                            </dl>
                                        </td>
                                        <td
                                            class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                        >
                                            <template v-if="flag.last_used_at">
                                                <zen-date-time
                                                    human
                                                    :value="flag.last_used_at"
                                                />
                                                <div class="text-xs lg:text-sm">
                                                    <zen-date-time
                                                        :value="
                                                            flag.last_used_at
                                                        "
                                                    />
                                                </div>
                                            </template>
                                            <span v-else>Never</span>
                                        </td>
                                    </tr>
                                    <tr v-if="flags.length === 0">
                                        <td colspan="2">
                                            <zen-empty-table-message
                                                compact
                                                data-testid="missing-default-empty"
                                                icon="ZenIconAlertWarning"
                                            >
                                                No flags match the current
                                                filters.
                                            </zen-empty-table-message>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>

                    <zen-pagination
                        v-if="!loading && !error"
                        :page="pagination.page"
                        :total-pages="pagination.total_pages"
                        :total="pagination.total"
                        @change="changePage"
                    />

                    <div v-if="!loading" class="text-sm pb-10">
                        Report Generated:
                        <zen-date-time :value="generatedAt" />
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import ReportService from '@/services/report.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { usePlan } from '@/composables/usePlan'
import { useSessionStore } from '@/stores/session'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { useServerPagination } from '@/composables/useServerPagination'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

const USAGE_STATUS_OPTIONS = [
    { value: 'in_use', label: 'In use' },
    { value: 'never_used', label: 'Never used' },
]

export default {
    components: { ProjectChangeNotice },
    name: 'MissingDefaultValue',
    setup() {
        const { current } = useCurrentEnvironment()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()
        const { pagination, setPage, resetPage, applyResponse, resetOnError } =
            useServerPagination({ perPage: 20 })
        return {
            current,
            hasPlanFeature,
            loading,
            error,
            run,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            resetOnError,
        }
    },
    data() {
        return {
            summary: {},
            flags: [],
            generatedAt: null,
            environmentKeyForLinks: this.current.environmentKey,
            filters: {
                environmentKey: this.current.environmentKey,
                usageStatus: '',
                sortBy: 'last_used_at',
                sortDir: 'desc',
            },
        }
    },
    computed: {
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        sessionStore() {
            return useSessionStore()
        },
        hasLifecycleManagement() {
            return this.hasPlanFeature('flag_lifecycle_management')
        },
        usageStatusOptions() {
            if (this.hasLifecycleManagement) return USAGE_STATUS_OPTIONS

            // "Never used" is lifecycle/hygiene tooling ("flags to clean up") - the backend
            // silently excludes those flags for a plan without it, so don't offer a filter
            // that would only ever return an empty list.
            return USAGE_STATUS_OPTIONS.filter(
                (option) => option.value !== 'never_used'
            )
        },
        summaryTiles() {
            const tiles = [
                { key: 'missing_default_count', label: 'Missing Default' },
                { key: 'in_use_count', label: 'In Use' },
                { key: 'never_used_count', label: 'Never Used' },
            ]

            if (this.hasLifecycleManagement) return tiles

            return tiles.filter((tile) => tile.key !== 'never_used_count')
        },
        environments() {
            const project = this.sessionStore.data?.projects?.data?.find(
                (p) => p.key === this.current.projectKey
            )

            return project?.environments?.data ?? []
        },
        sortIndicator() {
            return this.filters.sortDir === 'asc' ? '▲' : '▼'
        },
    },
    watch: {
        environmentScopeKey() {
            this.applyFilters()
        },
    },
    methods: {
        toggleSort(sortBy) {
            if (this.filters.sortBy === sortBy) {
                this.filters.sortDir =
                    this.filters.sortDir === 'asc' ? 'desc' : 'asc'
            } else {
                this.filters.sortBy = sortBy
                this.filters.sortDir = 'desc'
            }

            this.applyFilters()
        },
        applyFilters() {
            this.resetPage()
            this.load()
        },
        changePage(page) {
            this.setPage(page)
            this.load()
        },
        load() {
            // A plan without lifecycle management can't filter to "never used" - the
            // usageStatusOptions already hide that choice going forward, but a stale
            // filter value from before a downgrade would otherwise 404 the request.
            const usageStatus =
                !this.hasLifecycleManagement &&
                this.filters.usageStatus === 'never_used'
                    ? undefined
                    : this.filters.usageStatus || undefined

            this.run(
                (signal) =>
                    ReportService.getMissingDefaultValue(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            environmentKey:
                                this.filters.environmentKey || undefined,
                            usageStatus,
                            sortBy: this.filters.sortBy,
                            sortDir: this.filters.sortDir,
                            page: this.pagination.page,
                            perPage: this.pagination.per_page,
                        },
                        { signal }
                    ),
                (response) => {
                    this.summary = response.data.summary
                    this.flags = response.data.flags
                    this.environmentKeyForLinks = response.data.environment.key
                    this.generatedAt = response.data.generated_at
                    if (this.applyResponse(response.data.pagination)) {
                        this.load()
                    }
                },
                () => {
                    this.summary = {}
                    this.flags = []
                    this.resetOnError()
                }
            )
        },
    },
    mounted() {
        this.load()
    },
}
</script>
